import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["nonMerchandiseIdIdentifier", "fallbackSeleniumIdentifier", "queryParamTerm", "productConfigurations", "skipInterstitialPage", "redirectQueryParams"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { UPGRADE_SOURCE } from 'pricing-pages-lib/constants/QueryParams';
import { getCheckoutInterstitialUIUrl, getPricingPageProductRoute, navigateTo } from 'pricing-pages-lib/utils/urls';
import { PurchaseMotionCTA } from 'purchase-motions-components/components/PurchaseMotionCTA/PurchaseMotionCTA';
import { getRequestedProductAsQueryParams } from 'purchase-motions-components/utils/queryParams';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getQueryParams, stringifyQueryParams } from 'pricing-pages-lib/utils/queryParamUtils';
// @ts-expect-error dependency missing types
import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { BUY_ADDITIONAL_CONTACTS, BUY_ADDITIONAL_SEATS, BUY_TOUCHLESS, TALK_TO_SALES, TALK_TO_SALES_FALLBACK, TRIAL } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import UIPurchaseMotionButton from 'ui-addon-upgrades/button/UIPurchaseMotionButton';
import { useContainsProductLevels } from '../../../hooks/useContainsProductLevels';
import { useDisplayedCurrencyCode } from '../../../hooks/useCurrencies';
import useIsCheckoutUIFreeToProEnabled from '../../../hooks/useIsCheckoutUIFreeToProEnabled';
import useIsCheckoutUIFreeToStarterEnabled from '../../../hooks/useIsCheckoutUIFreeToStarterEnabled';
import { usePurchaseOrderGeneration } from '../../../hooks/usePurchaseOrderGeneration';
import { useRequestedProductsAdditionalSeats } from '../../../hooks/useRequestedProductsAdditionalSeats';
import { useTrackingFlow } from '../../../hooks/useTrackingFlow';
import { useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { isUngatedForPurchaseMotionCTASelector, isUngatedForSeatsSelector } from '../../../selectors/GatingSelectors';
import { isMarketingContactsSidegradeSelector } from '../../../selectors/MarketingContactsSelectors';
import { productsFromProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { crmSuiteProductLevelPreSeatsAndSeatsOnlySelector, rawProductListSelector } from '../../../selectors/ProductsSelectors';
import { trackingProductNameSelector } from '../../../selectors/TrackingSelectors';
import { Metrics } from '../../../utils/metrics';
import { trackBuyButtonClick, trackTalkToSalesClick } from '../../../utils/tracking/trackingEventUtils';
import { useQueryParamFromLocation } from '../../../utils/urls/appQueryParamUtils';
import InterstitialMarketingContactsPurchaseMotionButton from '../../interstitial/InterstitialMarketingContactsPurchaseMotionButton';
import ToInterstitialPurchaseMotionButtonWrapper from './ToInterstitialPurchaseMotionButtonWrapper';
import useIsEligibleForCheckoutInterstitialUI from './useIsEligibleForCheckoutInterstitialUI';
import { jsx as _jsx } from "react/jsx-runtime";
const setupMetricCounter = upgradeData => {
  Metrics.counter('buy-now-clicked', {
    source: upgradeData && upgradeData.uniqueId || 'PMBW'
  }).increment();
};
const filteredPropsForPMB = props => {
  const propsToPass = _objectWithoutPropertiesLoose(props, _excluded);
  return propsToPass;
};
const PurchaseMotionButtonWrapper = props => {
  var _props$purchaseMotion;
  const {
    productConfigurations = [],
    nonMerchandiseIdIdentifier,
    fallbackSeleniumIdentifier,
    queryParamTerm,
    onClick,
    skipInterstitialPage,
    trackingSource,
    purchaseMotionLocation
  } = props;
  const displayedCurrencyCode = useDisplayedCurrencyCode();
  const purchaseMotion = (_props$purchaseMotion = props.purchaseMotion) !== null && _props$purchaseMotion !== void 0 ? _props$purchaseMotion : props.purchaseMotions[props.level - 1];
  const crmSuiteProductLevel = useSelector(state => crmSuiteProductLevelPreSeatsAndSeatsOnlySelector(state, {
    skuIds: productConfigurations.map(({
      skuId
    }) => skuId)
  }));
  const crmSuiteSeleniumId = crmSuiteProductLevel && `crm-suite-${crmSuiteProductLevel}`;
  const {
    requestedProducts,
    terms
  } = usePurchaseOrderGeneration({
    productConfigurations,
    queryParamTerm
  });
  const apiName = useMemo(() => {
    var _requestedProducts$;
    // look for apiName in direct sources first
    const directApiName = props.apiName || props.upgradeData.apiName || props.upgradeData.upgradeProduct && UpgradeProductToApiNameMap[props.upgradeData.upgradeProduct];
    if (directApiName) {
      return directApiName;
    }

    // fallback to requested products
    return (_requestedProducts$ = requestedProducts[0]) === null || _requestedProducts$ === void 0 ? void 0 : _requestedProducts$.productApiName;
  }, [props.apiName, props.upgradeData, requestedProducts]);
  const isMarketingContactsSidegrade = useAppSelector(state => isMarketingContactsSidegradeSelector(state, productConfigurations));
  const isAppAuth = useAppSelector(state => isAppAuthSelector(state));
  const isSeatsUngated = useAppSelector(state => isUngatedForSeatsSelector(state));
  const isPreSeats = !isSeatsUngated;
  const productIdentifier = crmSuiteSeleniumId || nonMerchandiseIdIdentifier || (apiName === null || apiName === void 0 ? void 0 : apiName.toString()) || fallbackSeleniumIdentifier;
  const rawProductList = useSelector(rawProductListSelector);
  const isUngatedForPurchaseMotionCTA = useAppSelector(isUngatedForPurchaseMotionCTASelector);
  const {
    isCheckoutUIFreeToStarterEnabled,
    isInExperiment: isInExperimentSsbu0004,
    logExposure: logExposureSsbu0004
  } = useIsCheckoutUIFreeToStarterEnabled(productConfigurations);
  const {
    isCheckoutUIFreeToProEnabled,
    isInExperiment: isInExperimentSsbu0008,
    logExposure: logExposureSsbu0008
  } = useIsCheckoutUIFreeToProEnabled(productConfigurations);
  const {
    isEligibleForCheckoutInterstitialUI,
    targetFlow
  } = useIsEligibleForCheckoutInterstitialUI(productConfigurations);
  const isCheckoutUIFreeToStarterOrProEnabled = purchaseMotion === BUY_TOUCHLESS && (isCheckoutUIFreeToStarterEnabled || isCheckoutUIFreeToProEnabled);
  const requestedProductsAdditionalSeats = useRequestedProductsAdditionalSeats({
    productConfigurations
  });

  // combine requested products and requested products additional seats for isCheckoutUIFreeToStarterOrProEnabled cohorts
  const adjustedRequestedProducts = useMemo(() => {
    if (!isCheckoutUIFreeToStarterOrProEnabled) {
      return requestedProducts;
    }
    const combinedRequestedProducts = new Map();
    [...requestedProductsAdditionalSeats, ...requestedProducts].forEach(({
      productApiName,
      totalQuantity
    }) => {
      const existingProduct = combinedRequestedProducts.get(productApiName);
      if (!existingProduct || totalQuantity > existingProduct.totalQuantity) {
        combinedRequestedProducts.set(productApiName, {
          productApiName,
          totalQuantity
        });
      }
    });
    return Array.from(combinedRequestedProducts.values());
  }, [isCheckoutUIFreeToStarterOrProEnabled, requestedProductsAdditionalSeats, requestedProducts]);
  const trackingProductNames = useSelector(state => productConfigurations.map(({
    skuId
  }) => trackingProductNameSelector(state, {
    skuId
  })));
  const {
    containsStarter,
    containsProfessional
  } = useContainsProductLevels(productConfigurations);
  const ownedProducts = useSelector(state => productsFromProductInventorySelector(state)).map(p => p.apiName);
  const flow = useTrackingFlow({
    productConfigurations,
    purchaseMotion
  });
  const handleClick = () => {
    onClick === null || onClick === void 0 || onClick();
    switch (purchaseMotion) {
      case BUY_TOUCHLESS:
        setupMetricCounter(props.upgradeData);
        trackBuyButtonClick({
          purchaseMotion,
          activeCurrencyCode: displayedCurrencyCode,
          productNames: trackingProductNames,
          ownedProducts,
          trackingSource: props.trackingSource,
          screen: props.screen
        });

        // If the purchase motion is on the interstitial page, we don't want to log the exposure.
        if (purchaseMotionLocation === 'PRICING_PAGE_INTERSTITIAL') {
          return;
        }
        if (isInExperimentSsbu0004 && containsStarter) {
          logExposureSsbu0004();
        }
        if (isInExperimentSsbu0008 && containsProfessional) {
          logExposureSsbu0008();
        }
        break;
      case TALK_TO_SALES:
      case TALK_TO_SALES_FALLBACK:
        trackTalkToSalesClick({
          flow,
          activeCurrencyCode: displayedCurrencyCode,
          productNames: trackingProductNames,
          ownedProducts,
          trackingSource: props.trackingSource,
          screen: props.screen
        });
        break;
      default:
    }
  };
  const redirectQueryParams = useMemo(() => {
    if (isCheckoutUIFreeToStarterOrProEnabled && purchaseMotion === BUY_TOUCHLESS) {
      return Object.assign({}, props.redirectQueryParams, {
        isAccordionFreeToStarterOrProEnabled: 'true'
      });
    }
    return props.redirectQueryParams;
  }, [isCheckoutUIFreeToStarterOrProEnabled, purchaseMotion, props.redirectQueryParams]);

  // handle all button text overrides
  const buttonText = useMemo(() => {
    if (purchaseMotion === TRIAL) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.pricingCtas.startTrial"
      });
    }
    if (isPreSeats && isAppAuth && purchaseMotion === BUY_TOUCHLESS && isMarketingContactsSidegrade && purchaseMotionLocation !== 'PRICING_PAGE_INTERSTITIAL') {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "pricingPage.pricingCtas.switchToMarketingContacts"
      });
    }
    if (props._textOverride) {
      return props._textOverride;
    }
    return props.children;
  }, [purchaseMotion, isPreSeats, isAppAuth, isMarketingContactsSidegrade, purchaseMotionLocation, props._textOverride, props.children]);
  const upgradeSource = useQueryParamFromLocation(UPGRADE_SOURCE);
  const pricingPageRoute = getPricingPageProductRoute(isAppAuth);

  /**
   * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
   * LEGACY PURCHASE MOTION BUTTON
   * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
   */
  if (!isUngatedForPurchaseMotionCTA) {
    const propsToPass = Object.assign({}, props, {
      apiName,
      currencyCode: displayedCurrencyCode,
      requestedProducts: adjustedRequestedProducts,
      terms,
      'data-selenium-id': `${trackingSource}-${productIdentifier}-${purchaseMotion}-button`,
      nonMerchandiseIdIdentifier: crmSuiteSeleniumId || nonMerchandiseIdIdentifier,
      truncate: true,
      shouldShowErrorAlert: true,
      products: rawProductList,
      location: purchaseMotionLocation,
      onClick: handleClick,
      passthroughProps: {
        [BUY_TOUCHLESS]: {
          redirectQueryParams
        }
      },
      disabled: props.disabled,
      upgradeData: upgradeSource ? Object.assign({}, props.upgradeData, {
        upgradeSource
      }) : props.upgradeData
    });
    const isOnInterstitialPage = purchaseMotionLocation === 'PRICING_PAGE_INTERSTITIAL';
    const isEligibleForInterstitial = purchaseMotion === BUY_ADDITIONAL_CONTACTS || purchaseMotion === BUY_ADDITIONAL_SEATS || isSeatsUngated && purchaseMotion === BUY_TOUCHLESS;
    const shouldGoToInterstitial = !isOnInterstitialPage && !skipInterstitialPage && !isCheckoutUIFreeToStarterOrProEnabled && isEligibleForInterstitial;
    if (shouldGoToInterstitial) {
      return /*#__PURE__*/_jsx(ToInterstitialPurchaseMotionButtonWrapper, Object.assign({}, propsToPass, {
        children: buttonText
      }));
    }

    // Pre-seats and Seats users on the interstitial page should see a button that take them to checkout for marketing contacts
    if (purchaseMotionLocation === 'PRICING_PAGE_INTERSTITIAL' && purchaseMotion === BUY_ADDITIONAL_CONTACTS && productConfigurations.length > 0) {
      return /*#__PURE__*/_jsx(InterstitialMarketingContactsPurchaseMotionButton, {
        upgradeData: props.upgradeData,
        productConfigurations: productConfigurations,
        onClick: propsToPass.onClick,
        trackingSource: props.trackingSource,
        requestedProducts: propsToPass.requestedProducts,
        terms: propsToPass.terms
      });
    }
    return /*#__PURE__*/_jsx(UIPurchaseMotionButton, Object.assign({}, filteredPropsForPMB(propsToPass), {
      children: buttonText
    }));
  }

  /**
   * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
   * NEW PURCHASE MOTION CTA
   * !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
   */
  return /*#__PURE__*/_jsx(PurchaseMotionCTA, {
    apiName: apiName,
    location: purchaseMotionLocation,
    trackingData: {
      app: props.upgradeData.app,
      screen: props.upgradeData.screen,
      uniqueId: props.upgradeData.uniqueId,
      source: props.upgradeData.source || props.upgradeData.uniqueId
    },
    uiButtonProps: Object.assign({}, undefined !== props.block && {
      block: props.block
    }, undefined !== props.use && {
      use: props.use
    }, undefined !== props.size && {
      size: props.size
    }, undefined !== props.disabled && {
      disabled: props.disabled
    }),
    "data-selenium-id": `${trackingSource}-${productIdentifier}-${purchaseMotion}-button`,
    purchaseMotion: purchaseMotion,
    onClick: handleClick,
    ctaProps: {
      currencyCode: displayedCurrencyCode,
      requestedProducts: adjustedRequestedProducts,
      terms,
      redirectQueryParams,
      shouldDisplayAlertOnFailure: true,
      skipInterstitial: skipInterstitialPage || isCheckoutUIFreeToStarterOrProEnabled,
      interstitialQueryParams: {
        pricingPageProductRoute: pricingPageRoute || ''
      },
      onInterstitialRedirect: () => {
        if (isEligibleForCheckoutInterstitialUI && targetFlow) {
          const queryParams = getQueryParams();
          const stringifiedParams = stringifyQueryParams(Object.assign({}, queryParams, getRequestedProductAsQueryParams(adjustedRequestedProducts), {
            currencyCode: displayedCurrencyCode,
            pricingPageProductRoute: pricingPageRoute || '',
            purchaseMotion,
            term: queryParamTerm
          }));

          // invoke our own navigateTo on next tick after the buttons interstitial redirect happens
          setTimeout(() => {
            navigateTo(getCheckoutInterstitialUIUrl(stringifiedParams, targetFlow));
          }, 0);
        }
      }
    },
    children: buttonText
  });
};
export default PurchaseMotionButtonWrapper;